/**
 * BookingCalendarView — simple day-list view of upcoming bookings.
 * Groups bookings by date, shows contact name + service + resource,
 * offers quick actions (cancel · complete · no-show).
 * Kept intentionally light — a full swimlane view can layer on later.
 */
import { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, User, Check, X, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

function statusBadge(status) {
  const map = {
    confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    completed: 'bg-blue-50 text-blue-700 border-blue-200',
    cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
    no_show: 'bg-red-50 text-red-700 border-red-200',
  };
  return <Badge variant="outline" className={map[status] || ''}>{status.replace('_', ' ')}</Badge>;
}

export default function BookingCalendarView({ bookings, resources, onReload }) {
  const [filter, setFilter] = useState('upcoming'); // upcoming | today | past | all

  const resourceMap = useMemo(() => {
    const m = {};
    resources.forEach((r) => { m[r.id] = r; });
    return m;
  }, [resources]);

  const now = new Date();
  const filtered = bookings.filter((b) => {
    const start = new Date(b.start_at);
    if (filter === 'today') return start.toDateString() === now.toDateString();
    if (filter === 'upcoming') return start >= now && b.status !== 'cancelled';
    if (filter === 'past') return start < now;
    return true;
  });

  // Group by date
  const grouped = filtered.reduce((acc, b) => {
    const day = new Date(b.start_at).toDateString();
    (acc[day] = acc[day] || []).push(b);
    return acc;
  }, {});

  const cancel = async (bid) => {
    if (!window.confirm('Cancel this booking?')) return;
    await axios.post(`${API}/api/booking/bookings/${bid}/cancel`, { reason: 'Cancelled by operator' }, auth());
    toast.success('Booking cancelled');
    onReload();
  };
  const complete = async (bid) => {
    await axios.post(`${API}/api/booking/bookings/${bid}/complete`, {}, auth());
    toast.success('Booking marked complete');
    onReload();
  };
  const noShow = async (bid) => {
    await axios.patch(`${API}/api/booking/bookings/${bid}`, { status: 'no_show' }, auth());
    toast.success('Marked as no-show');
    onReload();
  };

  return (
    <div className="space-y-4" data-testid="booking-calendar">
      {/* Filter chips */}
      <div className="flex gap-2 flex-wrap">
        {['upcoming', 'today', 'past', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === f ? 'bg-emerald-brand text-white border-emerald-brand' : 'bg-white border-gray-200'}`}
            data-testid={`cal-filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {Object.keys(grouped).length === 0 && (
        <div className="text-center py-16">
          <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-medium">No bookings for this filter.</p>
        </div>
      )}

      {Object.entries(grouped).map(([day, list]) => (
        <div key={day}>
          <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 sticky top-0 bg-white/95 backdrop-blur py-1">
            {day}
          </div>
          <div className="space-y-2">
            {list.sort((a, b) => new Date(a.start_at) - new Date(b.start_at)).map((b) => {
              const start = new Date(b.start_at);
              const end = new Date(b.end_at);
              const timeStr = `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
              return (
                <Card key={b.id} className="hover:shadow-md transition-shadow" data-testid={`booking-${b.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-text-primary">{timeStr}</span>
                          {statusBadge(b.status)}
                        </div>
                        <div className="text-sm text-gray-medium mt-1">
                          {b.service_name} · {resourceMap[b.resource_id]?.label || 'Unassigned'}
                        </div>
                      </div>
                      {b.status !== 'cancelled' && b.status !== 'completed' && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => complete(b.id)} data-testid={`complete-${b.id}`}>
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => noShow(b.id)} data-testid={`noshow-${b.id}`}>
                            <AlertCircle className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => cancel(b.id)} data-testid={`cancel-${b.id}`}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
